/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import CommentInput from 'components/molecules/comment_input';
import AiManagementUpdateNew from 'components/molecules/ai_management_update_new';
import { BiddingData } from 'store/modules/bidding_store/types';
import { AiManagementUpdate } from 'services/ai_management_updates';
import { CreateUpdateInput } from 'services/actions/ai_management_updates';
import { biddingFileHref } from 'services/actions/ai_management_files';
import {
  buildBiddingUpdates,
  BiddingUpdateMessage,
  BiddingUpdateCategory,
  UPDATE_CATEGORY_ICON,
  UPDATE_FILTERS,
} from 'services/ai_management_bidding_updates';
import { formatDateTimeLong } from 'services/ai_management_dates';
import { Container } from './styles';

const PAGE_SIZE = 10;

const renderMessage = (message: BiddingUpdateMessage) => (
  <div key={message.id} className="update-message mb-2">
    <div className="update-message-head text-muted">
      {message.label}
      {message.label && ' • '}
      {formatDateTimeLong(message.created_at)}
    </div>
    {message.text}
  </div>
);

interface AiManagementBiddingUpdatesProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  manualUpdates: AiManagementUpdate[];
  onCreateUpdate: (input: Omit<CreateUpdateInput, 'cardId'>) => Promise<unknown>;
  onAddNote: (updateId: number, text: string) => Promise<unknown>;
  openNewUpdate?: boolean;
  initialUpdateId?: string;
}

const AiManagementBiddingUpdates: React.FC<AiManagementBiddingUpdatesProps> = ({
  bidding,
  loading,
  error,
  manualUpdates,
  onCreateUpdate,
  onAddNote,
  openNewUpdate = false,
  initialUpdateId,
}) => {
  const findInitialUpdate = () =>
    initialUpdateId
      ? buildBiddingUpdates(bidding, manualUpdates).find((update) => update.id === initialUpdateId)
      : undefined;

  const [category, setCategory] = useState<BiddingUpdateCategory | 'todas'>('todas');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = findInitialUpdate();
    return new Set(initial && !initial.from_client ? [initial.id] : []);
  });
  const [openId, setOpenId] = useState<string | null>(() => {
    if (openNewUpdate) return null;
    const initial = findInitialUpdate();
    return initial?.from_client ? initial.id : null;
  });
  const [creating, setCreating] = useState(!!openNewUpdate);
  const [page, setPage] = useState(1);
  const [savingNote, setSavingNote] = useState(false);
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const updates = useMemo(
    () => buildBiddingUpdates(bidding, manualUpdates),
    [bidding, manualUpdates],
  );

  const rows = useMemo(
    () =>
      category === 'todas' ? updates : updates.filter((update) => update.category === category),
    [updates, category],
  );

  const openUpdate = updates.find((update) => update.id === openId) ?? null;

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds = pageRows.map((update) => update.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const toggleOne = (id: string) => setSelected((prev) => toggleIn(prev, id));
  const toggleExpanded = (id: string) => setExpanded((prev) => toggleIn(prev, id));

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const openDetail = (id: string) => {
    setCreating(false);
    setOpenId((current) => (current === id ? null : id));
  };

  const startCreating = () => {
    setOpenId(null);
    setCreating(true);
  };

  const handleSave = (input: Omit<CreateUpdateInput, 'cardId'>) =>
    onCreateUpdate(input).then(() => {
      setCategory('todas');
      setPage(1);
      setCreating(false);
    });

  const handleAddNote = (text: string) => {
    const updateId = openUpdate?.manual_id;
    if (!updateId || !text.trim() || savingNote) return;

    setSavingNote(true);
    onAddNote(updateId, text.trim())
      .catch(() => window.alert('Não foi possível salvar a anotação.'))
      .finally(() => setSavingNote(false));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        Não foi possível carregar as atualizações.
      </div>
    );
  }

  return (
    <Container className="d-flex">
      <div className="updates-list flex-grow-1 overflow-auto p-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <h4 className="mb-0">Atualizações</h4>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center mr-2"
              onClick={startCreating}
            >
              <i className="material-icons mr-1">add</i>
              Adicionar atualização
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
              disabled={updates.length === 0}
            >
              <i className="material-icons mr-1">filter_list</i>
              Filtros
            </button>
          </div>
        </div>

        <div className="d-flex flex-wrap mb-3">
          {UPDATE_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`update-chip btn btn-sm rounded-pill mr-2 mb-2 ${
                category === filter.key ? 'btn-dark' : 'btn-outline-secondary'
              }`}
              onClick={() => setCategory(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {updates.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
            <label className="mb-0 d-flex align-items-center" htmlFor="select-all-updates">
              <input
                id="select-all-updates"
                type="checkbox"
                className="mr-2"
                checked={allPageSelected}
                onChange={toggleAllPage}
                disabled={pageIds.length === 0}
              />
              Selecionar todas as atualizações da página
            </label>

            <div className="d-flex">
              <button
                type="button"
                className="btn btn-secondary btn-sm d-flex align-items-center"
                disabled={selected.size === 0}
              >
                Ações
                <i className="material-icons ml-1">arrow_drop_down</i>
              </button>
            </div>
          </div>
        )}

        {rows.length === 0 && (
          <div className="text-muted py-3">Nenhuma atualização por aqui ainda.</div>
        )}

        {pageRows.map((update) => {
          const isExpanded = expanded.has(update.id);
          const isOpen = openId === update.id;
          const hasBody = !!update.description || update.messages.length > 0;

          return (
            <div
              key={update.id}
              className={`update-card bg-white mb-2 ${isOpen ? 'update-card-active' : ''}`}
            >
              <div className="d-flex align-items-center p-3">
                <input
                  type="checkbox"
                  className="mr-3 flex-shrink-0"
                  checked={selected.has(update.id)}
                  onChange={() => toggleOne(update.id)}
                  aria-label={`Selecionar ${update.title}`}
                />

                <span className="update-icon rounded d-flex align-items-center justify-content-center flex-shrink-0 mr-3">
                  <i className="material-icons text-muted">
                    {UPDATE_CATEGORY_ICON[update.category]}
                  </i>
                </span>

                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="update-title font-weight-bold mr-2">{update.title}</span>
                    {update.is_new && <span className="badge badge-danger mr-2">Novo</span>}
                    {update.status && (
                      <span className="badge badge-light border">{update.status}</span>
                    )}
                  </div>
                  {update.author && <div className="update-author text-muted">{update.author}</div>}
                </div>

                <div className="d-flex align-items-center flex-shrink-0 ml-3">
                  <span className="update-date text-muted text-nowrap mr-3">
                    {formatDateTimeLong(update.created_at)}
                  </span>
                  {update.from_client ? (
                    <button
                      type="button"
                      className={`btn btn-sm d-flex align-items-center ${
                        isOpen ? 'btn-success' : 'btn-secondary'
                      }`}
                      aria-label={`Abrir ${update.title}`}
                      onClick={() => openDetail(update.id)}
                    >
                      <i className="material-icons">chevron_right</i>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm d-flex align-items-center"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Recolher' : 'Expandir'} ${update.title}`}
                      disabled={!hasBody}
                      onClick={() => toggleExpanded(update.id)}
                    >
                      <i className="material-icons">{isExpanded ? 'expand_less' : 'expand_more'}</i>
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && hasBody && (
                <div className="update-body border-top px-3 py-3">
                  {update.description}
                  {update.messages.map(renderMessage)}
                </div>
              )}
            </div>
          );
        })}

        {totalPages > 1 && (
          <Pagination
            postsPerPage={PAGE_SIZE}
            totalPosts={rows.length}
            currentPage={currentPage}
            paginate={setPage}
            firstAndLastPage
          />
        )}
      </div>

      {creating && <AiManagementUpdateNew onClose={() => setCreating(false)} onSave={handleSave} />}

      {!creating && openUpdate && (
        <aside className="update-pane flex-shrink-0 border-left bg-white d-flex flex-column">
          <div className="d-flex align-items-start justify-content-between px-4 pt-4 pb-3">
            <h4 className="update-pane-title mb-0 mr-3">{openUpdate.title}</h4>
            <button
              type="button"
              className="btn btn-link text-dark p-0 d-flex align-items-center"
              aria-label="Fechar"
              onClick={() => setOpenId(null)}
            >
              <i className="material-icons">close</i>
            </button>
          </div>

          <div className="update-pane-body flex-grow-1 px-4 pb-4">
            {openUpdate.description && (
              <>
                <div className="update-field-label">Descrição</div>
                <p className="update-field-value mb-4">{openUpdate.description}</p>
              </>
            )}

            <div className="row">
              <div className="col-6">
                <div className="update-field-label">Criado por</div>
                <div className="update-field-value">{openUpdate.author ?? '—'}</div>
              </div>
              <div className="col-6">
                <div className="update-field-label">Data</div>
                <div className="update-field-value">
                  {formatDateTimeLong(openUpdate.created_at)}
                </div>
              </div>
            </div>

            <div className="update-section-title">Anexos</div>
            {openUpdate.attachments.length === 0 ? (
              <div className="text-muted">Nenhum anexo.</div>
            ) : (
              <div className="files-box border rounded bg-white">
                <table className="table files-table mb-0">
                  <thead>
                    <tr>
                      <th>Nome do arquivo</th>
                      <th className="file-date-col">Data</th>
                      <th className="file-actions-col" aria-label="Ações" />
                    </tr>
                  </thead>
                  <tbody>
                    {openUpdate.attachments.map((file) => {
                      const key = `${openUpdate.id}-${file.id}`;
                      const previewing = previewKey === key;

                      return (
                        <React.Fragment key={file.id}>
                          <tr>
                            <td className="text-truncate" title={file.filename}>
                              {file.filename}
                            </td>
                            <td className="text-nowrap">
                              {file.created_at ? formatDateTimeLong(file.created_at) : '—'}
                            </td>
                            <td className="file-actions text-right text-nowrap">
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm mr-1"
                                aria-label={`Excluir ${file.filename}`}
                              >
                                <i className="material-icons text-secondary">delete_outline</i>
                              </button>
                              <a
                                className="btn btn-outline-secondary btn-sm mr-1"
                                href={biddingFileHref(file.url)}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`Baixar ${file.filename}`}
                              >
                                <i className="material-icons text-secondary">file_download</i>
                              </a>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                aria-expanded={previewing}
                                aria-label={
                                  previewing ? 'Fechar pré-visualização' : 'Pré-visualizar arquivo'
                                }
                                onClick={() => setPreviewKey(previewing ? null : key)}
                              >
                                <i className="material-icons">
                                  {previewing ? 'expand_less' : 'expand_more'}
                                </i>
                              </button>
                            </td>
                          </tr>
                          {previewing && (
                            <tr className="file-preview-row">
                              <td colSpan={3} className="px-4 pb-3 pt-0">
                                <div className="file-preview-box d-flex flex-column">
                                  <AiManagementFilePreview
                                    name={file.filename}
                                    downloadUrl={file.url}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="update-section-title">Anotações</div>
            {openUpdate.messages.map(renderMessage)}
            <CommentInput
              isLoading={savingNote}
              createComment={handleAddNote}
              commentSource="biddings"
            />
          </div>
        </aside>
      )}
    </Container>
  );
};

export default AiManagementBiddingUpdates;
